import React, { useState, useEffect, useContext } from 'react';
import {
  Box, Button, Heading, Text, VStack, HStack, Badge, Spinner, Alert, AlertIcon,
  Card, CardHeader, CardBody, Divider, Link, Accordion, AccordionItem, AccordionButton,
  AccordionPanel, AccordionIcon, Input, IconButton, useToast, Modal, ModalOverlay,
  ModalContent, ModalHeader, ModalCloseButton, ModalBody, ModalFooter, useDisclosure,
  Textarea, RadioGroup, Radio
} from '@chakra-ui/react';
import { ExternalLinkIcon, AddIcon, DeleteIcon, CheckIcon, SearchIcon } from '@chakra-ui/icons';
import { callApi } from '../callApi';
import HeaderMenus from './HeaderMenus';
import { AppContext } from './AppContext';

const PayerCurationDashboard = () => {
  const { accessToken } = useContext(AppContext);
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();
  
  const [domains, setDomains] = useState({});
  const [domainStats, setDomainStats] = useState({});
  const [selectedDomain, setSelectedDomain] = useState(null);
  const [selectedConfig, setSelectedConfig] = useState(null);
  const [domainDocuments, setDomainDocuments] = useState([]);
  const [domainSuggestions, setDomainSuggestions] = useState([]);
  
  const [loading, setLoading] = useState(true);
  const [discovering, setDiscovering] = useState(false);
  const [discoveringAll, setDiscoveringAll] = useState(false);
  const [generatingLlmId, setGeneratingLlmId] = useState(null);
  const [testingUrl, setTestingUrl] = useState(null);
  const [testResult, setTestResult] = useState('');
  
  const [newVerifiedUrl, setNewVerifiedUrl] = useState('');
  const [newVerifiedTitle, setNewVerifiedTitle] = useState('');

  useEffect(() => {
    fetchDomains();
  }, [accessToken]);

  useEffect(() => {
    if (selectedConfig) {
      fetchDomainDocuments(selectedConfig.domain);
      fetchDomainSuggestions(selectedConfig.domain);
    }
  }, [selectedConfig]);

  const fetchDomains = async () => {
    setLoading(true);
    try {
      const response = await callApi('/api/v1/admin/payer_curation/domains', accessToken);
      const data = await response.json();
      if (data.status === 'success') {
        setDomains(data.domains);
        setDomainStats(data.domain_stats || {});
        if (Object.keys(data.domains).length > 0 && !selectedDomain) {
          setSelectedDomain(Object.keys(data.domains)[0]);
        }
      }
    } catch (err) {
      toast({ title: "Error fetching domains", status: "error" });
    } finally {
      setLoading(false);
    }
  };

  const fetchDomainDocuments = async (domain) => {
    try {
      const response = await callApi(`/api/v1/admin/payer_curation/documents/${domain}`, accessToken);
      const data = await response.json();
      if (data.status === 'success') {
        setDomainDocuments(data.documents);
      }
    } catch (err) {
      toast({ title: "Error fetching documents", status: "error" });
    }
  };

  const fetchDomainSuggestions = async (domain) => {
    try {
      const response = await callApi(`/api/v1/admin/payer_curation/suggestions/${domain}?topic=IONM`, accessToken);
      const data = await response.json();
      if (data.status === 'success') {
        setDomainSuggestions(data.suggestions);
      }
    } catch (err) {
      toast({ title: "Error fetching suggestions", status: "error" });
    }
  };

  const runDiscovery = async () => {
    if (!selectedConfig) return;
    setDiscovering(true);
    try {
      const response = await callApi(`/api/v1/admin/payer_curation/discover/${selectedConfig.id}`, accessToken, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ topic: 'IONM' })
      });
      const data = await response.json();
      if (data.status === 'success') {
        setDomainSuggestions(data.suggestions);
        toast({ title: "Discovery complete", status: "success" });
      }
    } catch (err) {
      toast({ title: "Discovery failed", status: "error" });
    } finally {
      setDiscovering(false);
    }
  };

  const runAllDiscovery = async () => {
    setDiscoveringAll(true);
    try {
      const response = await callApi(`/api/v1/admin/payer_curation/discover_all`, accessToken, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ topic: 'IONM' })
      });
      const data = await response.json();
      if (data.status === 'success') {
        toast({ title: "Background discovery started", description: "This will take several minutes. Check back later.", status: "info", duration: 5000 });
      } else {
        toast({ title: data.message || "Failed to start discovery", status: "error" });
      }
    } catch (err) {
      toast({ title: "Error starting discovery", status: "error" });
    } finally {
      setDiscoveringAll(false);
    }
  };

  const testExtraction = async (url) => {
    setTestingUrl(url);
    setTestResult('');
    onOpen();
    try {
      const response = await callApi('/api/v1/admin/payer_curation/test_extract', accessToken, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url })
      });
      const data = await response.json();
      if (data.status === 'success') {
        setTestResult(data.extracted_text);
      } else {
        setTestResult("Error: " + data.message);
      }
    } catch (err) {
      setTestResult("Failed to connect to server.");
    } finally {
      setTestingUrl(null);
    }
  };

  const updateConfigStatus = async (status) => {
    try {
      const response = await callApi(`/api/v1/admin/payer_curation/config/${selectedConfig.id}`, accessToken, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status })
      });
      const data = await response.json();
      if (data.status === 'success') {
        setSelectedConfig(data.config);
        fetchDomains();
        toast({ title: "Status updated", status: "success", duration: 2000 });
      }
    } catch (err) {
      toast({ title: "Update failed", status: "error" });
    }
  };

  const addDocument = async (url, title) => {
    if (!url) return;
    try {
      const response = await callApi('/api/v1/admin/payer_curation/document', accessToken, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ domain: selectedConfig.domain, url, title })
      });
      const data = await response.json();
      if (data.status === 'success') {
        setDomainDocuments([...domainDocuments, data.document]);
        setNewVerifiedUrl('');
        setNewVerifiedTitle('');
        toast({ title: "Document added", status: "success" });
      } else {
        toast({ title: data.error || "Failed to add document", status: "error" });
      }
    } catch (err) {
      toast({ title: "Error adding document", status: "error" });
    }
  };

  const deleteDocument = async (docId) => {
    if (!window.confirm("Are you sure you want to delete this document?")) return;
    try {
      const response = await callApi(`/api/v1/admin/payer_curation/document/${docId}`, accessToken, {
        method: 'DELETE'
      });
      const data = await response.json();
      if (data.status === 'success') {
        setDomainDocuments(domainDocuments.filter(d => d.id !== docId));
        toast({ title: "Document deleted", status: "success" });
      }
    } catch (err) {
      toast({ title: "Error deleting document", status: "error" });
    }
  };

  const updateDocument = async (docId, updates) => {
    try {
      const response = await callApi(`/api/v1/admin/payer_curation/document/${docId}`, accessToken, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updates)
      });
      const data = await response.json();
      if (data.status === 'success') {
        setDomainDocuments(domainDocuments.map(d => d.id === docId ? data.document : d));
        toast({ title: "Document updated", status: "success", duration: 2000 });
      }
    } catch (err) {
      toast({ title: "Update failed", status: "error" });
    }
  };

  const fetchDocumentRaw = async (docId) => {
    toast({ title: "Fetching raw text...", status: "info", duration: 2000 });
    try {
      const response = await callApi(`/api/v1/admin/payer_curation/document/${docId}/fetch_raw`, accessToken, {
        method: 'POST'
      });
      const data = await response.json();
      if (data.status === 'success') {
        setDomainDocuments(domainDocuments.map(d => d.id === docId ? data.document : d));
        toast({ title: "Raw text fetched", status: "success" });
      } else {
        toast({ title: data.message || "Failed to fetch raw text", status: "error" });
      }
    } catch (err) {
      toast({ title: "Error fetching raw text", status: "error" });
    }
  };

  const generateDocumentLlm = async (docId) => {
    setGeneratingLlmId(docId);
    toast({ title: "Generating LLM extraction...", status: "info", duration: 2000 });
    try {
      const response = await callApi(`/api/v1/admin/payer_curation/document/${docId}/generate_llm`, accessToken, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ topic: 'IONM' })
      });
      const data = await response.json();
      if (data.status === 'success') {
        setDomainDocuments(domainDocuments.map(d => d.id === docId ? data.document : d));
        toast({ title: "LLM extraction complete", status: "success" });
      } else {
        toast({ title: data.message || "Failed to generate LLM text", status: "error" });
      }
    } catch (err) {
      toast({ title: "Error generating LLM text", status: "error" });
    } finally {
      setGeneratingLlmId(null);
    }
  };

  if (loading && Object.keys(domains).length === 0) {
    return <Box p={10} textAlign="center"><Spinner size="xl" /></Box>;
  }

  return (
    <Box minH="100vh" bg="gray.50">
      <HeaderMenus />
      
      <HStack align="start" spacing={0} h="calc(100vh - 64px)">
        {/* Sidebar */}
        <Box w="300px" h="100%" bg="white" borderRight="1px solid" borderColor="gray.200" overflowY="auto">
          <Box p={4} borderBottom="1px solid" borderColor="gray.200" bg="gray.50">
            <VStack align="stretch" spacing={3}>
              <Heading size="sm">Payer Domains</Heading>
              <Button 
                size="xs" 
                colorScheme="blue" 
                variant="outline" 
                onClick={runAllDiscovery} 
                isLoading={discoveringAll}
                loadingText="Starting..."
              >
                Run All Discovery (Missing Only)
              </Button>
            </VStack>
          </Box>
          
          <Accordion allowMultiple defaultIndex={Object.keys(domains).map((_, i) => i)}>
            {Object.entries(domains).map(([domain, configs], idx) => (
              <AccordionItem key={domain}>
                <h2>
                  <AccordionButton _expanded={{ bg: 'blue.50', color: 'blue.700' }}>
                    <VStack align="start" spacing={1} flex="1">
                      <Box textAlign="left" fontWeight="bold" fontSize="sm">
                        {domain} ({configs.length})
                      </Box>
                      {domainStats[domain] && domainStats[domain].total > 0 && (
                        <HStack spacing={1} flexWrap="wrap">
                          <Badge colorScheme="gray" fontSize="3xs">Docs: {domainStats[domain].total}</Badge>
                          {domainStats[domain].raw > 0 && <Badge colorScheme="blue" fontSize="3xs">Raw: {domainStats[domain].raw}</Badge>}
                          {domainStats[domain].llm > 0 && <Badge colorScheme="teal" fontSize="3xs">LLM: {domainStats[domain].llm}</Badge>}
                          {domainStats[domain].manual > 0 && <Badge colorScheme="orange" fontSize="3xs">Man: {domainStats[domain].manual}</Badge>}
                        </HStack>
                      )}
                    </VStack>
                    <AccordionIcon />
                  </AccordionButton>
                </h2>
                <AccordionPanel pb={4} px={0}>
                  <VStack align="stretch" spacing={0}>
                    {configs.map(config => (
                      <Box 
                        key={config.id} 
                        p={3} 
                        pl={6}
                        cursor="pointer"
                        bg={selectedConfig?.id === config.id ? 'blue.100' : 'transparent'}
                        _hover={{ bg: 'gray.100' }}
                        onClick={() => setSelectedConfig(config)}
                        borderLeft={selectedConfig?.id === config.id ? '4px solid' : '4px solid transparent'}
                        borderColor="blue.500"
                      >
                        <HStack justify="space-between">
                          <Text fontSize="sm" fontWeight={selectedConfig?.id === config.id ? 'bold' : 'normal'}>
                            {config.state === 'ALL' ? 'National' : config.state} - {config.top_level}
                          </Text>
                          <Badge colorScheme={config.status === 'Verified' ? 'green' : 'yellow'} fontSize="2xs">
                            {config.status === 'Verified' ? 'V' : 'R'}
                          </Badge>
                        </HStack>
                      </Box>
                    ))}
                  </VStack>
                </AccordionPanel>
              </AccordionItem>
            ))}
          </Accordion>
        </Box>

        {/* Main Content */}
        <Box flex="1" h="100%" overflowY="auto" p={8}>
          {selectedConfig ? (
            <VStack align="stretch" spacing={6} maxW="5xl" mx="auto">
              
              {/* Header Card */}
              <Card shadow="sm">
                <CardHeader bg="white" borderBottomWidth="1px" pb={4}>
                  <HStack justify="space-between">
                    <VStack align="start" spacing={1}>
                      <Heading size="md">{selectedConfig.entity_name}</Heading>
                      <Text color="gray.500" fontSize="sm">Domain: {selectedConfig.domain} | State: {selectedConfig.state}</Text>
                    </VStack>
                    <HStack>
                      <Button 
                        colorScheme={selectedConfig.status === 'Verified' ? 'green' : 'yellow'}
                        onClick={() => updateConfigStatus(selectedConfig.status === 'Verified' ? 'Needs Review' : 'Verified')}
                      >
                        {selectedConfig.status === 'Verified' ? 'Mark as Needs Review' : 'Mark as Verified'}
                      </Button>
                    </HStack>
                  </HStack>
                </CardHeader>
                <CardBody>
                  <HStack>
                    <Text fontWeight="bold">Portal URL:</Text>
                    {selectedConfig.portal_url ? (
                      <Link href={selectedConfig.portal_url} isExternal color="blue.500">
                        {selectedConfig.portal_url} <ExternalLinkIcon mx="2px" />
                      </Link>
                    ) : (
                      <Text color="gray.500">None provided</Text>
                    )}
                  </HStack>
                </CardBody>
              </Card>

              {/* LLM Discovery Section */}
              <Card shadow="sm">
                <CardHeader pb={2}>
                  <HStack justify="space-between">
                    <Heading size="sm">LLM Suggested URLs</Heading>
                    <Button size="sm" leftIcon={<SearchIcon />} colorScheme="blue" variant="outline" onClick={runDiscovery} isLoading={discovering}>
                      Run Discovery
                    </Button>
                  </HStack>
                </CardHeader>
                <CardBody>
                  {domainSuggestions && domainSuggestions.length > 0 ? (
                    <VStack align="stretch" spacing={4}>
                      {domainSuggestions.map((item, idx) => (
                        <Box key={idx} p={4} bg="gray.50" borderRadius="md" borderWidth="1px">
                          <Text fontWeight="bold" fontSize="sm" mb={1}>{item.description}</Text>
                          <Link href={item.url} isExternal color="blue.500" fontSize="sm" wordBreak="break-all" display="block" mb={3}>
                            {item.url} <ExternalLinkIcon mx="2px" />
                          </Link>
                          <HStack>
                            <Button size="xs" onClick={() => testExtraction(item.url)}>Test Extract</Button>
                            <Button size="xs" colorScheme="green" leftIcon={<AddIcon />} onClick={() => addDocument(item.url, item.description)}>
                              Add to Curated Documents
                            </Button>
                          </HStack>
                        </Box>
                      ))}
                    </VStack>
                  ) : (
                    <Alert status="info" borderRadius="md">
                      <AlertIcon />
                      No suggested URLs found yet. Click "Run Discovery" to have the LLM search the portal.
                    </Alert>
                  )}
                </CardBody>
              </Card>

              {/* Curated Documents Section */}
              <Box>
                <Heading size="md" mb={4} color="purple.800">Curated Documents for {selectedConfig.domain}</Heading>
                
                {/* Add New Document Form */}
                <Card shadow="sm" mb={6} borderTop="4px solid" borderColor="green.400">
                  <CardBody>
                    <HStack>
                      <Input 
                        placeholder="Document Title (e.g., Medical Policy)" 
                        value={newVerifiedTitle}
                        onChange={(e) => setNewVerifiedTitle(e.target.value)}
                        size="sm"
                        w="30%"
                      />
                      <Input 
                        placeholder="Paste direct PDF or policy URL here..." 
                        value={newVerifiedUrl}
                        onChange={(e) => setNewVerifiedUrl(e.target.value)}
                        size="sm"
                        flex="1"
                      />
                      <Button size="sm" colorScheme="green" leftIcon={<AddIcon />} onClick={() => addDocument(newVerifiedUrl, newVerifiedTitle)}>
                        Add Document
                      </Button>
                    </HStack>
                  </CardBody>
                </Card>

                {/* Document Cards */}
                <VStack align="stretch" spacing={6}>
                  {domainDocuments.map(doc => (
                    <Card key={doc.id} shadow="md" borderLeft="4px solid" borderColor={doc.active_text_source && doc.active_text_source !== 'disabled' ? 'green.400' : 'gray.300'}>
                      <CardHeader bg="gray.50" pb={3}>
                        <HStack justify="space-between">
                          <VStack align="start" spacing={1}>
                            <Heading size="sm">{doc.title || 'Untitled Document'}</Heading>
                            <Link href={doc.url} isExternal color="blue.500" fontSize="xs" wordBreak="break-all">
                              {doc.url} <ExternalLinkIcon mx="2px" />
                            </Link>
                          </VStack>
                          <IconButton size="sm" colorScheme="red" variant="ghost" icon={<DeleteIcon />} onClick={() => deleteDocument(doc.id)} />
                        </HStack>
                      </CardHeader>
                      <CardBody>
                        <VStack align="stretch" spacing={4}>
                          
                          {/* Action Buttons */}
                          <HStack>
                            <Button size="xs" colorScheme="purple" onClick={() => fetchDocumentRaw(doc.id)}>
                              1. Fetch Raw Text (Tavily)
                            </Button>
                            <Button 
                              size="xs" 
                              colorScheme="teal" 
                              onClick={() => generateDocumentLlm(doc.id)} 
                              isDisabled={!doc.raw_full_text}
                              isLoading={generatingLlmId === doc.id}
                              loadingText="Generating..."
                            >
                              2. Generate LLM Extraction
                            </Button>
                          </HStack>

                          {/* Text Comparison Accordions */}
                          <Accordion allowMultiple>
                            <AccordionItem>
                              <h2>
                                <AccordionButton bg="gray.50">
                                  <Box flex="1" textAlign="left" fontWeight="bold" fontSize="sm">Raw Scrape (Tavily)</Box>
                                  <AccordionIcon />
                                </AccordionButton>
                              </h2>
                              <AccordionPanel pb={4}>
                                <Box bg="gray.100" p={4} borderRadius="md" maxH="300px" overflowY="auto">
                                  <Text whiteSpace="pre-wrap" fontFamily="monospace" fontSize="xs">
                                    {doc.raw_full_text || "No raw text fetched yet."}
                                  </Text>
                                </Box>
                              </AccordionPanel>
                            </AccordionItem>

                            <AccordionItem>
                              <h2>
                                <AccordionButton bg="teal.50">
                                  <Box flex="1" textAlign="left" fontWeight="bold" color="teal.800" fontSize="sm">LLM Cleaned Extraction</Box>
                                  <AccordionIcon />
                                </AccordionButton>
                              </h2>
                              <AccordionPanel pb={4}>
                                <Box bg="white" p={4} borderRadius="md" maxH="300px" overflowY="auto" borderWidth="1px" borderColor="teal.200">
                                  <Text whiteSpace="pre-wrap" fontFamily="monospace" fontSize="sm">
                                    {doc.llm_extracted_text || "No LLM extraction generated yet."}
                                  </Text>
                                </Box>
                              </AccordionPanel>
                            </AccordionItem>

                            <AccordionItem>
                              <h2>
                                <AccordionButton bg="orange.50">
                                  <Box flex="1" textAlign="left" fontWeight="bold" color="orange.800" fontSize="sm">Manual Override</Box>
                                  <AccordionIcon />
                                </AccordionButton>
                              </h2>
                              <AccordionPanel pb={4}>
                                <VStack align="end">
                                  <Textarea 
                                    value={doc.manual_extracted_text || ''}
                                    onChange={(e) => {
                                      const newDocs = domainDocuments.map(d => d.id === doc.id ? {...d, manual_extracted_text: e.target.value} : d);
                                      setDomainDocuments(newDocs);
                                    }}
                                    placeholder="Paste exact medical necessity rules, indications, and codes here..."
                                    minH="200px"
                                    fontFamily="monospace"
                                    fontSize="sm"
                                  />
                                  <Button size="sm" colorScheme="orange" onClick={() => updateDocument(doc.id, { manual_extracted_text: doc.manual_extracted_text })}>
                                    Save Manual Text
                                  </Button>
                                </VStack>
                              </AccordionPanel>
                            </AccordionItem>
                          </Accordion>

                          {/* Active Source Selector */}
                          <Box bg="blue.50" p={3} borderRadius="md" borderWidth="1px" borderColor="blue.200">
                            <Text fontWeight="bold" mb={2} color="blue.800" fontSize="sm">Active Source for Pipeline</Text>
                            <RadioGroup 
                              value={doc.active_text_source || 'disabled'} 
                              onChange={(val) => updateDocument(doc.id, { active_text_source: val })}
                            >
                              <HStack spacing={6}>
                                <Radio value="raw" bg="white" size="sm">Raw Scrape</Radio>
                                <Radio value="llm" bg="white" size="sm">LLM Extraction</Radio>
                                <Radio value="manual" bg="white" size="sm">Manual Override</Radio>
                                <Radio value="disabled" bg="white" size="sm">Disabled (Do not use)</Radio>
                              </HStack>
                            </RadioGroup>
                          </Box>

                        </VStack>
                      </CardBody>
                    </Card>
                  ))}
                  
                  {domainDocuments.length === 0 && (
                    <Alert status="info">
                      <AlertIcon />
                      No curated documents for this domain yet. Add one above or move a suggested URL.
                    </Alert>
                  )}
                </VStack>
              </Box>

            </VStack>
          ) : (
            <Box textAlign="center" py={20}>
              <Heading size="md" color="gray.400">Select a payer from the sidebar to begin curation.</Heading>
            </Box>
          )}
        </Box>
      </HStack>

      {/* Extraction Test Modal */}
      <Modal isOpen={isOpen} onClose={onClose} size="4xl" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent h="80vh">
          <ModalHeader>Tavily Extraction Test</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text fontSize="sm" color="gray.500" mb={4} wordBreak="break-all">Testing URL: {testingUrl}</Text>
            {testingUrl && !testResult ? (
              <Box textAlign="center" py={10}>
                <Spinner size="xl" mb={4} />
                <Text>Extracting full page text...</Text>
              </Box>
            ) : (
              <Box bg="gray.100" p={4} borderRadius="md" h="full" overflowY="auto">
                <Text whiteSpace="pre-wrap" fontFamily="monospace" fontSize="sm">
                  {testResult}
                </Text>
              </Box>
            )}
          </ModalBody>
          <ModalFooter>
            <Button colorScheme="blue" onClick={onClose}>Close</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

    </Box>
  );
};

export default PayerCurationDashboard;
